import { useCallback, useRef } from 'react';

type UseStableRoundedTimestampOptions = {
  roundingDirection?: 'up' | 'down';
  minutesToRound?: number;
};

/**
 * Calculates a rounded timestamp based on the current time.
 *
 * @param roundingDirection (optional - defaults to `'down'`) - Direction to round the timestamp (up or down)
 * @param minutesToRound (optional - defaults to `5`) - Number of minutes to round to (e.g., 5 will round to nearest 5 minutes)
 * Any value provided less than 1 will be treated as 1 minute to avoid division by zero or negative numbers.
 * @returns {Date} A new Date object with minutes rounded according to the specified options
 */
export const calculateRoundedTimestamp = ({
  roundingDirection = 'down',
  minutesToRound = 5,
}: UseStableRoundedTimestampOptions = {}): Date => {
  const now = new Date();
  const safeMinutesToRound = Math.max(1, minutesToRound); // Ensure minutesToRound is at least 1
  const minutes =
    (roundingDirection === 'down'
      ? Math.floor(now.getMinutes() / safeMinutesToRound)
      : Math.ceil(now.getMinutes() / safeMinutesToRound)) * safeMinutesToRound;
  now.setMinutes(minutes);
  now.setSeconds(0);
  now.setMilliseconds(0);

  return now;
};

/**
 * React hook that provides a stable rounded timestamp.
 * The timestamp is calculated once during component initialization and remains stable
 * across renders unless explicitly recalculated.
 *
 * @param roundingDirection (optional - defaults to `'down'`) - Direction to round the timestamp (up or down)
 * @param minutesToRound (optional - defaults to `5`) - Number of minutes to round to (e.g., 5 will round to nearest 5 minutes)
 * Any value provided less than 1 will be treated as 1 minute to avoid division by zero or negative numbers.
 * @returns An object containing:
 *   - timestamp: The stable rounded Date object
 *   - recalculate: Function to force recalculation of the timestamp in the ref, and return the new rounded timestamp
 */
export const useStableRoundedTimestamp = ({
  roundingDirection = 'down',
  minutesToRound = 5,
}: UseStableRoundedTimestampOptions = {}): { timestamp: Date; recalculate: () => Date } => {
  const ref = useRef(calculateRoundedTimestamp({ roundingDirection, minutesToRound }));

  const recalculate = useCallback(() => {
    const newTimestamp = calculateRoundedTimestamp({ roundingDirection, minutesToRound });
    ref.current = newTimestamp;
    return newTimestamp;
  }, [ref, roundingDirection, minutesToRound]);

  return { timestamp: ref.current, recalculate };
};
